import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Stack,
  IconButton,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';

/**
 * ProviderDialog manages the list of service providers used
 * in the fleet app.
 *
 * It loads providers from the API when opened, supports adding new providers,
 * editing existing ones, and deleting with confirmation.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {() => void} props.onClose - Called when the main dialog should close.
 * @param {string} props.API_BASE - Base URL for the backend API.
 * @param {string} props.token - Auth token used for API calls.
 * @returns {JSX.Element}
 */
export default function ProvidersDialog({ open, onClose, API_BASE, token }) {
  const [providers, setProviders] = useState([]);
  const [name, setName] = useState('');
  const [confirm, setConfirm] = useState({ open: false, id: null, name: '' });
  const [editing, setEditing] = useState({ id: null, name: '' });
  const auth = { headers: { Authorization: `Bearer ${String(token ?? '').trim()}` } };

  /**
   * Loads the current provider list from the backend and updates state.
   * Shows an error if the request fails.
   */
  const load = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/providers`, auth);
      setProviders(data?.rows || []);
    } catch {
      toast.error('Failed to load providers');
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  /**
   * Adds a new provider using the current "name" field and refreshes the list.
   * Clears the input and shows success or error messages based on the API result.
   */
  const add = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await axios.post(`${API_BASE}/api/providers`, { name: trimmed }, auth);
      setName('');
      await load();
      toast.success('Provider saved');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save provider');
    }
  };

  /**
   * Opens the delete confirmation dialog for the selected provider.
   *
   * @param {{ ProviderId: number | string, ProviderName: string }} prov
   */
  const askDelete = (prov) => setConfirm({ open: true, id: prov.ProviderId, name: prov.ProviderName });

  /**
   * Confirms deletion of the selected provider, refreshes the list, and closes the confirm dialog.
   * Shows a message for success or failure.
   */
  const doDelete = async () => {
    try {
      await axios.delete(`${API_BASE}/api/providers/${confirm.id}`, auth);
      setConfirm({ open: false, id: null, name: '' });
      await load();
      toast.success('Provider deleted');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to delete provider');
    }
  };

  /**
   * Enters inline edit mode for a provider.
   *
   * @param {{ ProviderId: number, ProviderName: string }} prov
   */
  const startEdit = (prov) => setEditing({ id: prov.ProviderId, name: prov.ProviderName });

  /**
   * Saves the edited provider name via the API and refreshes the list.
   */
  const saveEdit = async () => {
    const trimmed = editing.name.trim();
    if (!trimmed) return;
    try {
      await axios.put(`${API_BASE}/api/providers/${editing.id}`, { name: trimmed }, auth);
      setEditing({ id: null, name: '' });
      await load();
      toast.success('Provider updated');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update provider');
    }
  };

  const cancelEdit = () => setEditing({ id: null, name: '' });

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Manage Service Providers</DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              label="New provider name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
              fullWidth
              autoFocus
            />
            <Button variant="contained" onClick={add}>Add</Button>
          </Stack>

          <List dense>
            {providers.map((p) => (
              <ListItem
                key={p.ProviderId}
                divider
                sx={{ pr: 12 }}
                secondaryAction={
                  editing.id === p.ProviderId ? (
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        edge="end"
                        aria-label="save edit"
                        onClick={saveEdit}
                        size="small"
                      >
                        <CheckIcon color="success" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="cancel edit"
                        onClick={cancelEdit}
                        size="small"
                      >
                        <CloseIcon />
                      </IconButton>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        edge="end"
                        aria-label="edit provider"
                        onClick={() => startEdit(p)}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete provider"
                        onClick={() => askDelete(p)}
                        size="small"
                      >
                        <DeleteIcon color="error" fontSize="small" />
                      </IconButton>
                    </Stack>
                  )
                }
              >
                {editing.id === p.ProviderId ? (
                  <TextField
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                    size="small"
                    variant="standard"
                    fullWidth
                    autoFocus
                  />
                ) : (
                  <ListItemText primary={p.ProviderName} />
                )}
              </ListItem>
            ))}
          </List>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false, id: null, name: '' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete provider?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{confirm.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ open: false, id: null, name: '' })}>
            Cancel
          </Button>
          <Button onClick={doDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
